import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Table } from '@react-apps/design-system';
import type { ColumnDef } from '@react-apps/design-system';
import type { ContentItem } from '../../domain/entities/content.entity';
import { useExams } from '../hooks/use-exams';
import { useInstitutionsById } from '../hooks/use-institutions';
import { readExamBody } from '../exam-body';
import { buildExamCsvTemplate, downloadTextFile } from '../exam-csv-template';
import {
  ContentStatusBadge,
  InstitutionCell,
  formatDateTime,
  Mono,
} from '../display';
import { Pagination } from './Pagination';
import { RefreshButton } from '@/shared/RefreshButton';

const PER_PAGE = 10;

/**
 * Listagem de provas: o mesmo `GET /admin/content/items` do painel de
 * conteúdos, fixado em `type=EXAM` e com as colunas que só fazem sentido para
 * prova (instituição, ano, variante). A sigla+UF é resolvida no cliente a
 * partir do cache de instituições — `body.institutionId` é só um id.
 */
export function ExamsPanel() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [includeArchived, setIncludeArchived] = useState(false);
  // Nasce em `desc`: a pergunta que a tela responde é "o que entrou por
  // último". É o mesmo default do back, então a primeira carga não manda
  // parâmetro nenhum e continua batendo com o que a coluna mostra.
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const { data: institutionsById } = useInstitutionsById();

  const { data, isLoading, refetch, isFetching } = useExams({
    page,
    perPage: PER_PAGE,
    includeArchived,
    sortBy: 'createdAt',
    sortDirection,
  });

  /**
   * **Alterna, em vez de seguir a direção que o `Table` calcula.**
   *
   * O `Table` cicla `null → asc → desc → null`, e esse `null` não existe aqui:
   * o back sempre ordena (default `createdAt` desc), então "sem ordenação"
   * mostraria a mesma lista de `desc` com o ícone dizendo o contrário. Como a
   * tela já nasce em `desc`, seguir o ciclo dele levaria de `desc` para `null`
   * — e colapsar o `null` de volta em `desc` deixava o clique sem efeito
   * nenhum (foi o que o teste pegou). Ignorar a direção recebida e alternar é
   * o que expressa os dois estados que existem.
   *
   * Voltar para a página 1 é obrigatório: a página 3 de uma ordem não tem
   * relação com a página 3 da outra.
   */
  const handleSort = () => {
    setSortDirection((prev) => (prev === 'desc' ? 'asc' : 'desc'));
    setPage(1);
  };

  const columns: ColumnDef<ContentItem>[] = [
    { key: 'id', header: 'ID', render: (row) => <Mono>{row.id}</Mono> },
    {
      key: 'institution',
      header: 'Instituição',
      render: (row) => (
        <InstitutionCell
          institutionId={readExamBody(row).institutionId}
          byId={institutionsById}
        />
      ),
    },
    {
      key: 'year',
      header: 'Ano',
      render: (row) => readExamBody(row).year ?? '—',
    },
    {
      key: 'variant',
      header: 'Variante',
      render: (row) => readExamBody(row).variant || '—',
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <ContentStatusBadge status={row.status} />,
    },
    {
      key: 'createdAt',
      header: 'Criada em',
      sortable: true,
      render: (row) => (
        <span className="text-(--color-text-secondary)">
          {formatDateTime(row.createdAt)}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* Não existe `Checkbox` no design-system — input nativo é o fallback
            já usado nos outros painéis deste módulo. */}
        <label className="text-(--color-text-secondary) flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(e) => {
              setPage(1);
              setIncludeArchived(e.target.checked);
            }}
          />
          Ver também as arquivadas
        </label>
        <div className="flex shrink-0 items-center gap-2">
          <RefreshButton
            onRefresh={() => void refetch()}
            isRefreshing={isFetching}
          />
          <Button
            variant="secondary"
            onClick={() =>
              downloadTextFile(
                'modelo-importacao-prova.csv',
                buildExamCsvTemplate(institutionsById),
              )
            }
          >
            Baixar CSV modelo
          </Button>
          <Button onClick={() => void navigate('/content/exams/import')}>
            Importar prova de um CSV
          </Button>
        </div>
      </div>

      <Table
        columns={columns}
        data={data?.items ?? []}
        keyExtractor={(row) => row.id}
        loading={isLoading}
        emptyMessage="Nenhuma prova encontrada."
        striped
        sortColumn="createdAt"
        sortDirection={sortDirection}
        onSort={handleSort}
        onRowClick={(row) => void navigate(`/content/exams/${row.id}`)}
      />

      <Pagination
        page={page}
        total={data?.total ?? 0}
        perPage={PER_PAGE}
        loading={isLoading}
        label="prova"
        gender="f"
        onPageChange={setPage}
      />
    </div>
  );
}
